import React, { useEffect, useState } from 'react';
import EnhancedTable from 'src/components/enhanced-table/EnhancedTable';
import {
  EnhancedTableColumn,
  EnhancedTableColumnType,
  ITableMenuAction
} from 'src/components/enhanced-table';
import { DataService } from '../../services/data-service';
import Loader from 'src/components/Loader';
import { toast } from 'react-toastify';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import StatWidget from 'src/components/widgets/common/StatWidget';
import { Box, Paper, Typography, Stack, Button, Alert } from '@mui/material';
import SupplierDrawer from './supplier-drawer';
import { SpeakerGroupOutlined, Delete } from '@mui/icons-material';
import PermissionService, { ACTION, ROLE } from 'src/services/permission-service';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { Supplier, SupplierApiResponse, SuppliersApiResponse } from 'src/types/supplier.types';
import { OpenConfirmation } from 'src/redux/confirmation/confirmationReducer';
import { showAlert } from 'src/redux/alert/alertReducer';


const SupplierListing: React.FC = () => {
  const [suppliers, setSuppliers] = useState<Record<string, Supplier>>({});
  const [loading, setLoading] = useState(true);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [updateData, setUpdateData] = useState<Supplier | undefined>(undefined);
  const userRole = useAppSelector(s => s.user.user.role) as ROLE;
  const dispatch = useAppDispatch();

  const widgetVisibility = {
    totalSuppliers: false,
  };

  const initialize = async () => {
    setLoading(true);
    try {
      const res = await DataService.get('api/supplier');
      const response: SuppliersApiResponse = await res.json();
      const data: SupplierApiResponse[] = response.data || [];
      const result: Record<string, Supplier> = {};
      data.forEach((w) => {
        result[w.id] = {
          Id: w.id,
          Name: w.name
        };
      });
      setSuppliers(result);
    } catch (e) {
      toast.error('Failed to fetch suppliers');
    }
    setLoading(false);
  };

  useEffect(() => {
    initialize();
  }, []);

  const handleExport = async () => {
    try {
      const response = await DataService.get('api/supplier/export', {});
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'suppliers_export.xlsx';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        toast.error('Export failed');
      }
    } catch {
      toast.error('Export failed');
    }
  };


  const headers: EnhancedTableColumn[] = [
    {
      id: 'Name',
      label: 'Name',
      type: EnhancedTableColumnType.TEXT,
      editable: true,
      required: true,
    },
    {
      id: 'actions',
      label: 'Actions',
      type: EnhancedTableColumnType.ACTION,
      actions: [
         {
          onClick: (id: string) => {
            dispatch(OpenConfirmation({
              open: true,
              title: 'Delete Supplier',
              message: 'Are you sure you want to delete this supplier?',
              onSubmit: async () => {
                try {
                  const response = await DataService.delete(`api/Supplier/${id}`, {});
                  const data = await response.json();

                  if (response.ok && (data.Success === true || data.success === true)) {
                    dispatch(showAlert({ message: 'Supplier deleted successfully', type: 'success' }));
                    initialize();
                  } else {
                    const errorMessage = data.Message || data.message || 'Failed to delete supplier';
                    dispatch(showAlert({ message: errorMessage, type: 'error' }));
                  }
                } catch (error) {
                  dispatch(showAlert({ message: 'Failed to delete supplier', type: 'error' }));
                }
              }
            }));
                  },
                  icon: <Delete fontSize="small" />,
                  label: 'Delete',
                  visible: () => PermissionService.getInstance().isAllowed(ACTION.VIEW_ALL_SUPPLIERS, userRole),
                }
      ]
    }
  ];

  const handleAddSupplier = async (row: { Name: string }) => {
    const req = await DataService.post('api/Supplier/create', { Name: row.Name });
    const resJson = await req.json();
    if (req.ok && resJson.success) {
      await initialize();
      return true;
    } else {
      throw new Error(resJson.message || 'An Error occurred!');
    }
  };

  const handleEditSupplier = async (row: { id: string, Name: string }) => {
    const req = await DataService.put(`api/Supplier/update/${row.id}`, { Name: row.Name });
    const resJson = await req.json();
    if (req.ok && resJson.success) {
      await initialize();
      return true;
    } else {
      throw new Error(resJson.message || 'An Error occurred!');
    }
  };

  const actions: ITableMenuAction[] = [
    {
      key: 'export',
      title: '',
      icon: <DownloadIcon />,
      disabled: (selected: string[]) => false,
      onClick: handleExport,
    }
  ];

  const dataObj: Record<string, any> = Object.fromEntries(
    Object.entries(suppliers).map(([id, supplier]) => [
      id,
      {
        ...supplier,
      }
    ])
  );


  if (loading) {
    return <Loader />;
  }

  return (
    <div >
      <title>Suppliers</title>
      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        {widgetVisibility.totalSuppliers && (
          <StatWidget
            title="Total Suppliers"
            value={Object.keys(suppliers).length}
            icon={<SpeakerGroupOutlined />}
            color="#1976d2"
            visible={widgetVisibility.totalSuppliers}
          />
        )}
      </div>
      <EnhancedTable
        title="Suppliers"
        columns={headers}
        data={dataObj}
        actions={actions}
        onAddRow={handleAddSupplier}
        onEditRow={handleEditSupplier}
        showEditOnTable={true}
        collapsible={true}
        wmsMode={true}
      />
    </div>
  );
};

export default SupplierListing;
